import { z } from 'zod';
/**
 * The internal log viewer's API (§5).
 *
 * ⚠️ **PLATFORM-SCOPED, and that is a data-protection boundary rather than a
 * convenience.** One store holds every tenant's diagnostics, so a tenant-facing
 * search would be a cross-tenant surface by construction. §5: *"Initial raw-log
 * access shall be limited to OIP platform administrators"*, and *"cross-tenant
 * authorization must be enforced by the backend, not only by the UI."*
 *
 * ⚠️ **NO `tenantId` IS TAKEN FROM A HEADER HERE.** `tenantId` below is a
 * FILTER over the logs — "show me lines about this tenant" — not a scope a
 * caller is confined to. The two look identical in a query string and mean
 * opposite things, which is why this file says so out loud.
 */
export const LOG_TIER_VALUES = ['debug', 'info', 'warn', 'error', 'security'];
export const logTierSchema = z.enum(LOG_TIER_VALUES);
/**
 * ⚠️ `.strict()` like every other query schema on this platform (R3): an
 * undocumented parameter is one an integrator cannot know about, and a silently
 * ignored filter reads as a search that found nothing.
 */
export const logSearchQuerySchema = z
    .object({
    /**
     * ⚠️ REQUIRED, both of them. `log_entry` is partitioned by day, so a query
     * with no bound touches every partition of every tier — over a thousand
     * relations at the security tier's retention. This is the one filter whose
     * absence is a performance fault rather than a wider search.
     */
    from: z.coerce.date(),
    to: z.coerce.date(),
    tier: z.union([logTierSchema, z.array(logTierSchema)]).optional(),
    /** Pino's numeric levels: 40 is "warn and worse". */
    levelAtLeast: z.coerce.number().int().min(10).max(60).optional(),
    service: z.union([z.string().min(1), z.array(z.string().min(1))]).optional(),
    env: z.string().min(1).optional(),
    tenantId: z.string().min(1).optional(),
    companyId: z.string().min(1).optional(),
    providerId: z.string().min(1).optional(),
    vehicleId: z.string().min(1).optional(),
    /*
     * ⚠️ A LIST, like `tier` and `service` beside it — and `code` was the odd one
     * out until §6's dashboard needed it. An area holds up to 21 codes, so a
     * single-code filter would make the drill-through show a FRACTION of what the
     * panel counted. That matters more than convenience: the drill-through is the
     * only check on `area`, the one registry field CI cannot verify, and a link
     * that shows a subset would quietly defeat it.
     */
    code: z.union([z.string().min(1), z.array(z.string().min(1))]).optional(),
    correlationId: z.string().min(1).optional(),
    requestId: z.string().min(1).optional(),
    traceId: z.string().min(1).optional(),
    jobId: z.string().min(1).optional(),
    /** Free text over the MESSAGE. §4: JSON is reachable through the declared filters only. */
    q: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    /** Opaque; hand back what the previous page returned. */
    cursor: z.string().min(1).optional(),
})
    .strict()
    .refine((v) => v.to > v.from, { message: '`to` must be after `from`', path: ['to'] });
export const logLineSchema = z.object({
    /**
     * ⚠️ A COMPOSITE reference, not the bare uuid, and it is not decoration. The
     * primary key is `(tier, at, id)`; a lookup by id alone cannot be partition-
     * pruned and scans the whole store — making the one read that must be instant
     * the slowest. Opaque to the client: hand it back to fetch the detail.
     */
    ref: z.string(),
    at: z.string(),
    tier: logTierSchema,
    level: z.number(),
    /** The label a person reads. Derived from `level`, never stored twice. */
    levelLabel: z.string(),
    service: z.string(),
    instance: z.string(),
    env: z.string(),
    version: z.string().nullable(),
    /** ⚠️ A STRING: `seq` is a bigint, wider than a JS number. */
    seq: z.string().nullable(),
    msg: z.string(),
    code: z.string().nullable(),
    correlationId: z.string().nullable(),
    requestId: z.string().nullable(),
    traceId: z.string().nullable(),
    spanId: z.string().nullable(),
    tenantId: z.string().nullable(),
    companyId: z.string().nullable(),
    partnerId: z.string().nullable(),
    userId: z.string().nullable(),
    vehicleId: z.string().nullable(),
    providerId: z.string().nullable(),
    jobId: z.string().nullable(),
    actorRole: z.string().nullable(),
    route: z.string().nullable(),
    status: z.number().nullable(),
    durationMs: z.number().nullable(),
    ipAddress: z.string().nullable(),
});
export const logSearchResponseSchema = z.object({
    lines: z.array(logLineSchema),
    /** Absent on the last page. */
    nextCursor: z.string().nullable(),
    /**
     * ⚠️ The window the SERVER searched, echoed back rather than left to the
     * client's memory of what it asked. An empty result is only readable as a
     * fact if the reader can see its bounds.
     */
    from: z.string(),
    to: z.string(),
});
export const logDetailResponseSchema = z.object({
    line: logLineSchema,
    /**
     * The rest of what the line carried.
     *
     * ⚠️ Already redacted — it was filtered by the allowlist BEFORE it was ever
     * stored, so this is not a second sanitising step and must not become one.
     * §5's "sanitized metadata" is satisfied at write time, which is the only
     * place it can be satisfied for stdout too.
     */
    meta: z.record(z.unknown()),
});
export const logTimelineQuerySchema = z
    .object({
    correlationId: z.string().min(1),
    /** The instant of the line the reader came from; the window centres on it. */
    around: z.coerce.date(),
    windowHours: z.coerce
        .number()
        .int()
        .min(1)
        .max(24 * 14)
        .default(72),
    limit: z.coerce.number().int().min(1).max(200).default(200),
})
    .strict();
export const logTimelineResponseSchema = z.object({
    lines: z.array(logLineSchema),
    from: z.string(),
    to: z.string(),
    /**
     * ⚠️ Reported, because a timeline is read to reconstruct what happened and one
     * missing its end without saying so is worse than one that refuses — a reader
     * draws a conclusion from it either way.
     */
    truncated: z.boolean(),
});
/**
 * §5: "Synchronous exports are limited to 10,000 rows."
 *
 * ⚠️ Larger exports run as asynchronous jobs with a download link, capped at
 * 1,000,000 — **not built**, and the API says so by refusing rather than by
 * quietly truncating. A truncated export is the worst outcome available here:
 * it is a file somebody takes away and treats as complete.
 */
export const LOG_EXPORT_SYNC_MAX_ROWS = 10_000;
export const LOG_EXPORT_FORMATS = ['csv', 'ndjson'];
export const logExportFormatSchema = z.enum(LOG_EXPORT_FORMATS);
/**
 * ⚠️ **DERIVED FROM THE SEARCH QUERY, never restated.** The two must offer the
 * same filters or the export is of a different question from the one on screen —
 * and the failure is silent in the direction that matters: a filter the search
 * honours and the export ignores yields a file with MORE in it than the reader
 * was looking at, which nothing about the download says.
 *
 * ⚠️ `limit` and `cursor` are OMITTED rather than ignored. `.strict()` means
 * sending one is a 400 that says so, where accepting-and-dropping would let a
 * caller believe they had bounded a download that is in fact bounded by the
 * server's own cap.
 */
export const logExportQuerySchema = logSearchQuerySchema
    .innerType()
    .omit({ limit: true, cursor: true })
    .extend({ format: logExportFormatSchema.default('csv') })
    .strict()
    .refine((v) => v.to > v.from, { message: '`to` must be after `from`', path: ['to'] });
// ── §1: runtime log levels ──────────────────────────────────────────────────
export const LOG_LEVEL_NAMES = ['trace', 'debug', 'info', 'warn', 'error', 'fatal'];
export const logLevelNameSchema = z.enum(LOG_LEVEL_NAMES);
/**
 * ⚠️ **AN OVERRIDE MUST EXPIRE, and the cap is part of the contract rather than
 * a suggestion.** Turning debug on in production is what somebody does during an
 * incident and never undoes; the capacity plan measures debug at 100% as
 * exceeding every other tier combined, so a forgotten override fills the store
 * and starts §8's ladder dropping partitions. Twenty-four hours is longer than
 * any debugging session and shorter than a weekend.
 */
export const LOG_LEVEL_MAX_MINUTES = 24 * 60;
export const logLevelSetRequestSchema = z
    .object({
    /** A logger name, a parent on a `:` boundary, or `*` for everything. */
    service: z.string().trim().min(1).max(64),
    level: logLevelNameSchema,
    minutes: z.coerce.number().int().min(1).max(LOG_LEVEL_MAX_MINUTES),
    reason: z.string().trim().max(500).optional(),
})
    .strict();
export const logLevelOverrideSchema = z.object({
    service: z.string(),
    level: z.string(),
    expiresAt: z.string(),
    /** ⚠️ Expired rows are LISTED, not hidden — see the response note below. */
    expired: z.boolean(),
    reason: z.string().nullable(),
    setBy: z.string().nullable(),
    setAt: z.string(),
});
export const logLevelStateSchema = z.object({
    /**
     * ⚠️ **What this ONE replica is currently set to**, and the response says so.
     * There are N of them and each polls independently, so a reader who took this
     * for "the platform's levels" would be wrong for up to one poll interval after
     * any change — and would have no way to tell.
     */
    effective: z.record(z.string()),
    overrides: z.array(logLevelOverrideSchema),
    /** The level every unoverridden service falls back to. */
    fallback: z.string(),
    debugSampleRate: z.number(),
    /** How long a change takes to reach every replica. */
    propagationSeconds: z.number(),
});
//# sourceMappingURL=logs.js.map